import { useEffect, useState } from 'react'
import type * as React from 'react'
import { BrandMark } from '@/components/BrandMark'
import { requiredEnvValue } from '@/lib/env'
import { applyDocumentBranding } from '@/lib/documentBranding'
import { fetchPublicBranding, rememberTenantSlug, type PublicBranding } from '@/lib/publicBranding'

// The booking view for the tenant slug entry (/:tenant) — the SAME screen for a
// guest and for a signed-in client, which is the point of it being one
// component rather than two. The widget flow (guided pickers: category →
// service → date → time, then the AI) is what a customer books through either
// way, so nothing about the experience is allowed to depend on whether they
// have an account.
//
// Exactly two things differ, and both are handed in as props:
//   * `authToken` — a Supabase JWT puts <hj-booking-widget> in signed-in mode.
//     v1-widget resolves the customer from it (web_user_id/email, same matching
//     as v1-chat), so name and phone are never asked for and every action the
//     AI takes is scoped to that customer.
//   * `headerAction` — "Sign in" for a guest, the account menu for a client.
//     A signed-in client with no way out is broken, and the guest needs the
//     door in; the slot is the same either way.
//
// The widget bundle is hosted by this app at /booking-widget.js (copied from
// widget/dist on every widget build), so neither path adds an external
// dependency.

const supabaseUrl = requiredEnvValue(process.env.EXPO_PUBLIC_SUPABASE_URL)
const WIDGET_SRC = '/booking-widget.js'

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'hj-booking-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        tenant: string
        'api-base': string
        'auth-token'?: string
      }
    }
  }
}

// Idempotent: the custom element upgrades in place once the script registers,
// so the <hj-booking-widget> below can render before the script has loaded.
function ensureWidgetScript() {
  if (customElements.get('hj-booking-widget')) return
  if (document.querySelector(`script[src="${WIDGET_SRC}"]`)) return
  const script = document.createElement('script')
  script.src = WIDGET_SRC
  script.defer = true
  document.head.appendChild(script)
}

export interface TenantBookingProps {
  slug: string
  /**
   * Supabase Auth JWT for a signed-in client, or null/undefined for a guest.
   *
   * Passed through as an attribute rather than captured once, because the
   * widget reads it fresh on every chat call: the SDK rotates the access token
   * roughly hourly, and v1-widget answers a stale one with a hard 401. Keep
   * this fed from `authState.session.access_token`, which useAuth re-renders on
   * TOKEN_REFRESHED — a token snapshotted at mount would break the booking
   * flow an hour in.
   */
  authToken?: string | null
  /** Right-hand header control: the sign-in link, or the account menu. */
  headerAction?: React.ReactNode
  /** One line above the widget. The guest door and the client door explain
   *  different things, and this is the only copy that differs between them. */
  intro?: React.ReactNode
}

export function TenantBooking({ slug, authToken, headerAction, intro }: TenantBookingProps) {
  const [business, setBusiness] = useState<PublicBranding | null>(null)

  useEffect(() => {
    ensureWidgetScript()
  }, [])

  // Branding comes from the public widget config on BOTH paths, deliberately:
  // it is the one endpoint that answers "who is this business" with or without
  // a session, so the header cannot render differently for a client than for a
  // guest. Cosmetic only — the page works with the fallbacks if this fails.
  //
  // A signed-in client is already inside BrandingProvider, which brands the
  // document from the tenant config. Re-applying here is a duplicate write of
  // the same tenant-record values, not a competing one.
  useEffect(() => {
    let cancelled = false
    fetchPublicBranding(slug).then((found) => {
      if (cancelled || !found) return
      setBusiness(found)
      applyDocumentBranding(found.name, found.brand_logo_url, slug)
      rememberTenantSlug(slug)
    })
    return () => {
      cancelled = true
    }
  }, [slug])

  const brandColor = business?.brand_primary_color || '#3b82f6'
  const businessName = business?.name ?? 'Book an appointment'

  return (
    <div className="flex h-full min-h-0 flex-col bg-muted">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-background px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <BrandMark
            name={businessName}
            logoUrl={business?.brand_logo_url}
            color={brandColor}
            size={32}
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{businessName}</p>
            {business?.brand_tagline && (
              <p className="truncate text-[11px] text-muted-foreground">{business.brand_tagline}</p>
            )}
          </div>
        </div>
        {headerAction}
      </header>

      <main className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-4 py-6">
        {intro && (
          <p className="mb-4 max-w-[420px] text-center text-sm text-muted-foreground">{intro}</p>
        )}
        <hj-booking-widget
          tenant={slug}
          api-base={`${supabaseUrl}/functions/v1`}
          // Omitted entirely for a guest — the widget treats an absent
          // attribute as "not signed in", so an empty string must not be sent.
          {...(authToken ? { 'auth-token': authToken } : {})}
          className="w-full"
        >
          {/* Shown only until the script upgrades the element (shadow DOM then takes over). */}
          <p className="py-8 text-center text-sm text-muted-foreground">Loading booking…</p>
        </hj-booking-widget>
      </main>
    </div>
  )
}
